import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { type TTicketMember } from '../types/ticket.type';
import { type IPagination } from '@/types/general.type';
import { type TFetchParams } from '@/types/fetch.type';

export type TGetListTicketMemberResponse = ApiResponse<IPagination<TTicketMember>>;
export type TGetListTicketMemberParams = TFetchParams & {
  spaceId: string;
};

export const getListTicketMember = async (
  params: TGetListTicketMemberParams
): Promise<TGetListTicketMemberResponse> => {
  const url = `/ticket/member/${params.spaceId}`;

  return axiosInstance.get(url, { params });
};

export const GET_LIST_TICKET_MEMBER_QUERY_KEY = 'get-list-ticketing-ticket-member';

export const getListTicketMemberQueryOptions = <TData = TGetListTicketMemberResponse>(
  params: TGetListTicketMemberParams,
  select?: (data: TGetListTicketMemberResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_LIST_TICKET_MEMBER_QUERY_KEY, { spaceId: params?.spaceId }],
    queryFn: () => getListTicketMember(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListTicketMemberQuery<TData>(options: {
  params: TGetListTicketMemberParams;
  queryConfig?: QueryConfig<typeof getListTicketMemberQueryOptions<TData>>;
  select: (data: TGetListTicketMemberResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListTicketMemberQuery(options: {
  params: TGetListTicketMemberParams;
  queryConfig?: QueryConfig<typeof getListTicketMemberQueryOptions<TGetListTicketMemberResponse>>;
}): ReturnType<typeof useQuery<TGetListTicketMemberResponse>>;

// Implementation
export function useListTicketMemberQuery<TData = TGetListTicketMemberResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListTicketMemberParams;
  queryConfig?: QueryConfig<typeof getListTicketMemberQueryOptions<TData | TGetListTicketMemberResponse>>;
  select?: (data: TGetListTicketMemberResponse) => TData;
}) {
  return useQuery({
    ...getListTicketMemberQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListTicketMemberResponse ? TGetListTicketMemberResponse : TData>>;
}
